import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable, of } from "rxjs";
import { catchError, shareReplay, tap } from "rxjs/operators";

import { UserService, environment } from "@mypxplat/xplat/core";
import { WebCommunityService } from "./community.service";

/**
 * What a member gets for claiming the Discord gift, as resolved by the backend.
 *
 * Not derived here. `resolveGiftBenefit` in paeapi's discordController decides this, and the
 * same resolver runs on the redeem path — so the preview and the outcome cannot disagree. See
 * `docs/fsp-tmp-subs.md`.
 *
 *   license               a free perpetual Fender Studio Pro license
 *   license_extension     they own it already, so +12 months of updates on that license
 *   subscription_extension  they subscribe already, so 12 months of the subscription
 *   unsupported           on a biller we cannot extend; support has to move them first
 *   null                  we could not work it out — promise nothing
 */
export type DiscordGiftBenefit = "license" | "license_extension" | "subscription_extension" | "unsupported" | null;

/**
 * Whether the member has a Discord gift to claim, and what it is.
 *
 * This exists so every surface that advertises a PAID Fender Studio Pro offer can check first.
 * Showing someone 50% off a thing we are about to give them is the worst version of that page,
 * and the offer appears in several places — the dashboard's upgrades section, the Tone Master
 * Pro product page, any future one.
 *
 * Cached deliberately: the answer is the same for the whole page life, and without caching the
 * dashboard and product detail would each fire their own request for it. `refresh()` is for
 * after something that could change the answer, i.e. a claim.
 */
@Injectable({ providedIn: "root" })
export class DiscordGiftService {
  /** The backend's gift state, or null when it does not apply / could not be read. */
  public state: any = null;

  /**
   * There is something to claim, whatever it turns out to be. Gates the offer block itself.
   *
   * Bound directly by templates, so a plain property rather than a getter — change detection
   * re-reads it every cycle and a getter would recompute each time.
   */
  public canClaimGift = false;

  /**
   * The claim would produce a Fender Studio Pro LICENSE, free. Narrower than `canClaimGift` on
   * purpose: this is the one that suppresses paid Fender Studio Pro offers, and a subscriber
   * getting months is not getting a license — they may still want to buy the perpetual, so
   * their offers must stay.
   */
  public canClaimFreeFsp = false;

  /** Which benefit, for copy. See `DiscordGiftBenefit`. */
  public giftBenefit: DiscordGiftBenefit = null;

  /** Who bills them, populated for the subscription and unsupported cases only. */
  public giftBenefitProvider: string | null = null;

  /** Emits whenever the answer changes, for views that render before it resolves. */
  public state$ = new BehaviorSubject<any>(null);

  private _request: Observable<any> | null = null;

  constructor(
    private _community: WebCommunityService,
    private _userService: UserService
  ) {}

  /**
   * Resolves once and is reused. Answers null without a request when linking is switched off
   * (the paeapi routes 404 in that case) or nobody is signed in.
   */
  load(force = false): Observable<any> {
    if (!(environment.features as any)?.discordLinking || !this._userService.user?.id) {
      this._apply(null);
      return of(null);
    }
    if (!force && this.state) return of(this.state);
    if (!force && this._request) return this._request;

    this._request = this._community.getDiscordLink().pipe(
      tap((res: any) => this._apply(res)),
      // Fails OPEN — i.e. no free offer, so the paid one still shows. A member briefly seeing
      // a discount they could have had free is recoverable; hiding every paid offer because a
      // status call blipped is not.
      catchError(() => {
        this._apply(null);
        return of(null);
      }),
      shareReplay(1)
    );
    return this._request;
  }

  /** Re-read after something that changes the answer — a claim, or linking an account. */
  refresh(): Observable<any> {
    this.state = null;
    this._request = null;
    return this.load(true);
  }

  /** Drop everything on sign-out; the next account's answer is not this one's. */
  clear() {
    this._request = null;
    this._apply(null);
  }

  private _apply(res: any) {
    this.state = res;
    this.giftBenefit = res?.giftBenefit ?? null;
    this.giftBenefitProvider = res?.giftBenefitProvider ?? null;

    // Linked, nothing claimed yet, offer still open, and the hardware requirement met (or not
    // enforced).
    const meetsHardware = !res?.requiresToneMasterPro || res?.hasToneMasterPro !== false;
    const claimed = !!res?.giftRedemption?.redeemedAt || !!res?.giftRedeemed;
    // Guild membership, captured when the account was linked. `redeemGift` rejects an unverified
    // member outright, so without this the offer would advertise a claim that 403s. Read strictly
    // (missing ⇒ not verified) to match that endpoint, and never surfaced as a call to action:
    // the gift is for people who were already in the Discord, so a non-member is shown nothing
    // rather than told how to qualify.
    const verified = !!res?.link?.verified;
    const claimable = !!res?.linked && verified && !claimed && res?.giftOpen !== false && meetsHardware;

    // A benefit we could not resolve (`null`) shows nothing at all rather than an offer that
    // cannot name itself — which also means a web build that lands before the paeapi deploy
    // degrades to "feature off" instead of promising the wrong thing.
    this.canClaimGift = claimable && (this.giftBenefit === "license" || this.giftBenefit === "license_extension" || this.giftBenefit === "subscription_extension");
    this.canClaimFreeFsp = claimable && (this.giftBenefit === "license" || this.giftBenefit === "license_extension");

    this.state$.next(res);
  }
}
